import { AmethystClient } from '@/integrations/Immersve/AmethystClient';
import countryPhoneCodes from '../common/countryPhoneCodes.json';
import { ContactChannels } from './ContactChannels';
import type { KycSession } from './KycSession';
import type { ResidentialAddressDto } from '@/integrations/Immersve/types';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import type { KycSessionData } from '@/stores/auth.store';

export class Cardholder {
  accountId: string;

  constructor(accountId: string) {
    this.accountId = accountId;
  }

  static forKycSession(kycSession: KycSession) {
    return new Cardholder(kycSession.accountId);
  }

  async loadContactChannels(): Promise<ContactChannels> {
    const contactChannelsResponse = await new AmethystClient().getContactChannels(this.accountId);

    const emailAddress = contactChannelsResponse?.email?.emailAddress ?? '';
    const phoneNumber = contactChannelsResponse?.phone?.phoneNumber ?? '';

    let phoneNumberWithoutPrefix;

    const phonePrefixDetails = countryPhoneCodes.find((country) =>
      phoneNumber.startsWith(country.dialCode)
    );

    if (phonePrefixDetails) {
      phoneNumberWithoutPrefix = contactChannelsResponse?.phone.phoneNumber.split(
        phonePrefixDetails.dialCode
      )[1];
    }

    return ContactChannels.create({
      email: { emailAddress },
      phone: {
        number: phoneNumberWithoutPrefix || phoneNumber,
        prefix: phonePrefixDetails?.dialCode,
      },
    });
  }

  async loadCurrentResidentialAddress(): Promise<ResidentialAddressDto | undefined> {
    const residentialAddresses = await new AmethystClient().getResidentialAddresses(this.accountId);

    return residentialAddresses?.[0];
  }

  async saveResidentialAddress(residentialAddress: ResidentialAddressDto) {
    await new AmethystClient().updateResidentialAddresses({
      accountId: this.accountId,
      residentialAddress,
    });
  }

  async updateContactChannels(contactChannels: Required<ContactChannels>) {
    await new AmethystClient().updateContactChannels({
      emailAddress: contactChannels.email.emailAddress,
      phoneNumber: contactChannels.fullPhoneNumber,
      accountId: this.accountId,
    });
  }

  async updateExpectedSpend(expectedSpend: Required<string>) {
    await new AmethystClient().updateExpectedSpend({
      cardholderId: this.accountId,
      expectedSpendAmount: expectedSpend,
    });
  }

  saveConsentForRegion({ kycSessionId, region }: { kycSessionId: string; region: string }) {
    const currentConsents =
      KycSessionStorage.getDataForSession<KycSessionData>(kycSessionId).consentsGiven || [];
    if (!currentConsents?.includes(region)) {
      KycSessionStorage.updateDataForSession<KycSessionData>(kycSessionId, {
        consentsGiven: [...currentConsents, region],
      });
    }
  }

  hasConsentForRegion({ kycSessionId, region }: { kycSessionId: string; region: string }) {
    const currentConsents =
      KycSessionStorage.getDataForSession<KycSessionData>(kycSessionId).consentsGiven || [];
    return currentConsents?.includes(region);
  }

  async hasResidentialAddress() {
    return !!(await this.loadCurrentResidentialAddress());
  }
}
